import {
  FormControl as MUIFormControl,
  RadioGroup as MUIRadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import React from 'react';
import { Controller, FieldValues, useFormContext } from 'react-hook-form';

import { Alert } from '@/shared/components/ui/alert/alert';
import { FormErrorMessage } from '@/shared/components/ui/form/components/form-error-message/form-error-message';
import { FormLabel } from '@/shared/components/ui/form/components/form-label/form-label';
import { Radio } from '@/shared/components/ui/form/components/radio/radio';
import { RadioGroupOption } from '@/shared/components/ui/form/components/radio-group/radio-group.styled';
import {
  TRadioGroupProps,
  TRadioOption,
} from '@/shared/components/ui/form/components/radio-group/radio-group.types';
import { TruncatedText } from '@/shared/components/ui/truncated-text/truncated-text';

export const RadioGroup = <
  TFieldValues extends FieldValues,
  TValue extends string | number = string,
>({
  name,
  label,
  required,
  disabled,
  defaultValue,
  options,
  optionDirection = 'row',
  hint,
  onChange,
}: TRadioGroupProps<TFieldValues, TValue>) => {
  const { control } = useFormContext<TFieldValues>();

  // Described options need the room a fixed-width truncated label doesn't give.
  const hasDescriptions = options.some((option) => option.description);

  const renderOptionLabel = (option: TRadioOption<TValue>) => {
    if (!option.description) {
      return <TruncatedText variant="p8" maxLength={14} text={option.label} />;
    }

    return (
      <Stack>
        <Typography variant="p8">{option.label}</Typography>
        <Typography variant="p9" color="textAdditional">
          {option.description}
        </Typography>
      </Stack>
    );
  };

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({
        field: { onChange: onFieldChange, value, ...fieldProps },
        fieldState,
      }) => (
        <MUIFormControl>
          <FormLabel
            error={Boolean(fieldState?.error)}
            label={label}
            required={required}
          />
          <MUIRadioGroup
            value={value ?? ''}
            onChange={(event, value) => {
              onFieldChange(event, value);
              onChange?.();
            }}
            {...fieldProps}
          >
            <Stack
              direction={optionDirection}
              gap={optionDirection === 'row' ? '1rem' : '0.5rem'}
            >
              {options.map((option) => (
                <RadioGroupOption
                  key={option.value}
                  $error={Boolean(fieldState?.error)}
                  $wide={hasDescriptions}
                  value={option.value}
                  control={<Radio disabled={disabled} />}
                  label={renderOptionLabel(option)}
                />
              ))}
            </Stack>
          </MUIRadioGroup>
          {(fieldState?.error || hint) && (
            <Stack gap="0.5rem" marginTop="0.75rem">
              {fieldState?.error && <FormErrorMessage field={label} />}
              {hint && <Alert color="blue" text={hint} />}
            </Stack>
          )}
        </MUIFormControl>
      )}
    />
  );
};
